import { useCallback, useEffect, useRef, useState } from 'react';

import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';

import { normalizeConfidence } from '../../../presentations/confidence';
import RoleAssignmentIndicator from '../../../presentations/RoleAssignmentIndicator';
import { ROW_BADGE_HEIGHT_PX } from '../constants';
import { estimateTwoCharacterWidthPx } from '../utils';

export default function RoleTag({
  role,
  confidence,
}: {
  role: string;
  confidence: number | null;
}) {
  const roleRef = useRef<HTMLDivElement>(null);
  const [showText, setShowText] = useState(true);
  const hasConfidence = confidence !== null;
  const showUserAssignedFallbackRing = !hasConfidence;
  const confidencePercent = hasConfidence
    ? Math.round(normalizeConfidence(confidence) * 100)
    : null;
  const indicatorWidth = 16;

  const updateTextVisibility = useCallback(() => {
    const roleElement = roleRef.current;
    if (!roleElement) return;

    const styles = window.getComputedStyle(roleElement);
    const gap = parseFloat(styles.columnGap || styles.gap) || 0;
    const minTextWidth = estimateTwoCharacterWidthPx(roleElement);
    const availableTextWidth = roleElement.clientWidth - indicatorWidth - gap;
    const nextShowText = availableTextWidth >= Math.ceil(minTextWidth);

    setShowText((prev) => (prev === nextShowText ? prev : nextShowText));
  }, [indicatorWidth]);

  useEffect(() => {
    const roleElement = roleRef.current;
    if (!roleElement) return;

    const observer = new ResizeObserver(updateTextVisibility);
    observer.observe(roleElement);
    updateTextVisibility();
    return () => observer.disconnect();
  }, [updateTextVisibility]);

  return (
    <div
      ref={roleRef}
      className="flex min-w-0 items-center gap-1.5"
      style={{ minHeight: `${ROW_BADGE_HEIGHT_PX}px` }}
    >
      <Tooltip
        text={
          hasConfidence
            ? `Role Confidence ${confidencePercent}%`
            : showUserAssignedFallbackRing
              ? 'User-assigned role'
              : 'Role confidence unavailable'
        }
        side="top"
        inModal
      >
        <span className="inline-flex shrink-0">
          <RoleAssignmentIndicator
            assigned
            confidence={confidence}
          />
        </span>
      </Tooltip>
      {showText && (
        <Text variant="studio/body/medium/xs" className="truncate text-primary">
          {role}
        </Text>
      )}
    </div>
  );
}
